// Site-wide user geolocation, captured once per session on first page load and
// shared across routes (external store → components subscribe reactively).
// Used to sort/label providers by distance. Silent on denial; never throws.

export type UserCoords = { lat: number; lng: number };

// Rough bounding box covering the continental US + AK/HI/PR (our provider
// data is US-only). Used to skip auto-applying "near me" for clearly
// non-US visitors, so we don't show a "3,000 mi away" badge or an empty
// radius search for someone browsing from abroad.
export function isLikelyUS(c: UserCoords): boolean {
  return c.lat >= 15 && c.lat <= 72 && c.lng >= -180 && c.lng <= -60;
}

const COORDS_KEY = "hx_geo_coords_v1";
const ASKED_KEY = "hx_geo_asked_v1";

let current: UserCoords | null = null;
let hydrated = false;
const listeners = new Set<() => void>();

function readStored(): UserCoords | null {
  try {
    const raw = sessionStorage.getItem(COORDS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as { lat?: unknown; lng?: unknown };
    if (typeof o.lat === "number" && typeof o.lng === "number") return { lat: o.lat, lng: o.lng };
  } catch { /* ignore */ }
  return null;
}

/** Current coords, or null. Reads sessionStorage once so a fresh route pickup works. */
export function getUserCoords(): UserCoords | null {
  if (!hydrated && typeof window !== "undefined") {
    current = readStored();
    hydrated = true;
  }
  return current;
}

export function subscribeUserCoords(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function commit(c: UserCoords) {
  current = c;
  hydrated = true;
  try { sessionStorage.setItem(COORDS_KEY, JSON.stringify(c)); } catch { /* private mode */ }
  listeners.forEach((l) => l());
}

// Prompt for browser geolocation once per session (site-wide, called from the
// root). No-op if we already have coords or already asked this session.
export function ensureUserCoords(): void {
  if (typeof window === "undefined" || !navigator.geolocation) return;
  if (getUserCoords()) return;
  try {
    if (sessionStorage.getItem(ASKED_KEY)) return;
    sessionStorage.setItem(ASKED_KEY, "1");
  } catch { /* private mode — still ask */ }
  navigator.geolocation.getCurrentPosition(
    (pos) => commit({
      lat: Number(pos.coords.latitude.toFixed(4)),
      lng: Number(pos.coords.longitude.toFixed(4)),
    }),
    () => { /* denied / unavailable — stay non-geo */ },
    { enableHighAccuracy: false, timeout: 8000, maximumAge: 600_000 },
  );
}
